import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Waves,
  Trophy,
  Target,
  Gift,
  Users,
  Crown,
  Medal,
  Star,
  CheckCircle2,
  Clock,
  Anchor,
  Ship,
  Compass,
  Plus,
  Trash2,
  Edit
} from 'lucide-react';

const SeasonsPage = () => {
  const { user, api, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [activeSeason, setActiveSeason] = useState(null);
  const [allSeasons, setAllSeasons] = useState([]);
  const [myProgress, setMyProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [newSeason, setNewSeason] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    objectives: [],
    rewards: []
  });
  const [newObjective, setNewObjective] = useState({ id: '', title: '', description: '', points: 10 });

  useEffect(() => {
    fetchSeasonData();
  }, []);

  const fetchSeasonData = async () => {
    try {
      const [seasonRes, allSeasonsRes] = await Promise.all([
        api.get('/seasons/active'),
        api.get('/seasons')
      ]);
      
      setAllSeasons(allSeasonsRes.data || []);
      
      if (seasonRes.data) {
        setActiveSeason(seasonRes.data);
        setJoined(seasonRes.data.participants?.includes(user.id));
        
        if (seasonRes.data.participants?.includes(user.id)) {
          const [progressRes, leaderboardRes] = await Promise.all([
            api.get(`/seasons/${seasonRes.data.id}/progress`),
            api.get(`/seasons/${seasonRes.data.id}/leaderboard`)
          ]);
          setMyProgress(progressRes.data);
          setLeaderboard(leaderboardRes.data);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinSeason = async () => {
    if (!activeSeason) return;
    try {
      await api.post(`/seasons/${activeSeason.id}/join`);
      toast.success('🌊 Bienvenue dans la saison !');
      setJoined(true);
      fetchSeasonData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCompleteObjective = async (objectiveId) => {
    if (!activeSeason) return;
    try {
      const response = await api.post(`/seasons/${activeSeason.id}/complete-objective?objective_id=${objectiveId}`);
      toast.success(`🎯 +${response.data.points} points !`);
      fetchSeasonData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();
    try {
      await api.post('/seasons', {
        ...newSeason,
        start_date: new Date(newSeason.start_date).toISOString(),
        end_date: new Date(newSeason.end_date).toISOString()
      });
      toast.success('🌊 Nouvelle saison créée !');
      setCreateOpen(false);
      setNewSeason({ name: '', description: '', start_date: '', end_date: '', objectives: [], rewards: [] });
      fetchSeasonData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteSeason = async (seasonId) => {
    if (!confirm('Supprimer cette saison ? Cette action est irréversible.')) return;
    try {
      await api.delete(`/seasons/${seasonId}`);
      toast.success('Saison supprimée');
      fetchSeasonData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleEditSeason = (season) => {
    setEditingSeason(season);
    setNewSeason({
      name: season.name,
      description: season.description,
      start_date: season.start_date?.split('T')[0] || '',
      end_date: season.end_date?.split('T')[0] || '',
      objectives: season.objectives || [],
      rewards: season.rewards || []
    });
    setEditOpen(true);
  };

  const handleUpdateSeason = async (e) => {
    e.preventDefault();
    if (!editingSeason) return;
    try {
      await api.put(`/seasons/${editingSeason.id}`, {
        ...newSeason,
        start_date: new Date(newSeason.start_date).toISOString(),
        end_date: new Date(newSeason.end_date).toISOString()
      });
      toast.success('Saison mise à jour');
      setEditOpen(false);
      setEditingSeason(null);
      setNewSeason({ name: '', description: '', start_date: '', end_date: '', objectives: [], rewards: [] });
      fetchSeasonData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const addObjective = () => {
    if (!newObjective.id || !newObjective.title) return;
    setNewSeason({
      ...newSeason,
      objectives: [...newSeason.objectives, { ...newObjective }]
    });
    setNewObjective({ id: '', title: '', description: '', points: 10 });
  };

  const removeObjective = (index) => {
    setNewSeason({
      ...newSeason,
      objectives: newSeason.objectives.filter((_, i) => i !== index)
    });
  };

  const updateObjective = (index, field, value) => {
    const updatedObjectives = [...newSeason.objectives];
    updatedObjectives[index] = { ...updatedObjectives[index], [field]: value };
    setNewSeason({ ...newSeason, objectives: updatedObjectives });
  };

  const getDaysRemaining = () => {
    if (!activeSeason) return 0;
    const end = new Date(activeSeason.end_date);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const getProgressPercentage = () => {
    if (!activeSeason || !myProgress) return 0;
    const total = activeSeason.objectives?.length || 1;
    const completed = myProgress.objectives_completed?.length || 0;
    return Math.round((completed / total) * 100);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRankIcon = (index) => {
    if (index === 0) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (index === 1) return <Medal className="w-5 h-5 text-gray-400" />;
    if (index === 2) return <Medal className="w-5 h-5 text-amber-700" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-muted-foreground">{index + 1}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Waves className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement de la saison...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="seasons-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Waves className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl">Saisons</h1>
                <p className="text-sm text-muted-foreground">Défis mensuels de l'équipage</p>
              </div>
            </div>
          </div>

          {isAdmin && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-500 text-white hover:bg-blue-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle saison
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Créer une nouvelle saison</DialogTitle>
                  <DialogDescription>Définissez les objectifs et récompenses</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateSeason} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 col-span-2">
                      <Label>Nom de la saison *</Label>
                      <Input 
                        value={newSeason.name} 
                        onChange={(e) => setNewSeason({ ...newSeason, name: e.target.value })} 
                        placeholder="Ex: La Traversée de Janvier"
                        className="bg-secondary/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de début *</Label>
                      <Input 
                        type="date"
                        value={newSeason.start_date} 
                        onChange={(e) => setNewSeason({ ...newSeason, start_date: e.target.value })} 
                        className="bg-secondary/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de fin *</Label>
                      <Input 
                        type="date"
                        value={newSeason.end_date} 
                        onChange={(e) => setNewSeason({ ...newSeason, end_date: e.target.value })} 
                        className="bg-secondary/50"
                      />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Description</Label>
                      <Textarea 
                        value={newSeason.description} 
                        onChange={(e) => setNewSeason({ ...newSeason, description: e.target.value })} 
                        placeholder="Description de la saison..."
                        className="bg-secondary/50"
                        rows={2}
                      />
                    </div>
                  </div>

                  {/* Objectives */}
                  <div className="space-y-3">
                    <Label>Objectifs</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Input 
                        value={newObjective.id} 
                        onChange={(e) => setNewObjective({ ...newObjective, id: e.target.value })} 
                        placeholder="ID (ex: obj_1)"
                        className="bg-secondary/50"
                      />
                      <Input 
                        value={newObjective.title} 
                        onChange={(e) => setNewObjective({ ...newObjective, title: e.target.value })} 
                        placeholder="Titre"
                        className="bg-secondary/50"
                      />
                      <div className="flex gap-2">
                        <Input 
                          type="number"
                          value={newObjective.points} 
                          onChange={(e) => setNewObjective({ ...newObjective, points: parseInt(e.target.value) })} 
                          placeholder="Points"
                          className="bg-secondary/50 w-20"
                        />
                        <Button type="button" onClick={addObjective} variant="outline">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {newSeason.objectives.length > 0 && (
                      <div className="space-y-2">
                        {newSeason.objectives.map((obj, i) => (
                          <div key={obj.id || `obj-${i}`} className="flex items-center gap-2 p-2 bg-secondary/30 rounded text-sm">
                            <Target className="w-4 h-4 text-primary" />
                            <span className="flex-1">{obj.title}</span>
                            <Badge>{obj.points} pts</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
                    <Button type="submit" className="bg-blue-500 text-white">Créer la saison</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {!activeSeason ? (
          <Card className="card-base text-center py-12">
            <CardContent>
              <Ship className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-xl mb-2">Aucune saison active</h3>
              <p className="text-muted-foreground">La prochaine saison arrive bientôt. Restez à l'écoute !</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Season Header */}
            <Card className="card-base overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-primary/10" />
              <CardHeader className="relative">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge className="bg-blue-500/20 text-blue-500 mb-2">
                      <Waves className="w-3 h-3 mr-1" />
                      Saison en cours
                    </Badge>
                    <CardTitle className="text-2xl font-heading">{activeSeason.name}</CardTitle>
                    <CardDescription className="mt-2">{activeSeason.description}</CardDescription>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-blue-500">{getDaysRemaining()}</div>
                    <div className="text-sm text-muted-foreground">jours restants</div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative">
                {!joined ? (
                  <Button 
                    onClick={handleJoinSeason} 
                    className="w-full bg-blue-500 text-white hover:bg-blue-600"
                    data-testid="join-season-btn"
                  >
                    <Anchor className="w-4 h-4 mr-2" />
                    Rejoindre la saison
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Ta progression</span>
                      <span className="font-semibold">{getProgressPercentage()}%</span>
                    </div>
                    <Progress value={getProgressPercentage()} className="h-3" />
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">
                        <Target className="w-3 h-3 mr-1" />
                        {myProgress?.objectives_completed?.length || 0}/{activeSeason.objectives?.length || 0} objectifs
                      </Badge>
                      <Badge className="bg-primary/20 text-primary">
                        <Star className="w-3 h-3 mr-1" />
                        {myProgress?.points_earned || 0} points
                      </Badge>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Objectives */}
            {joined && activeSeason.objectives?.length > 0 && (
              <div className="space-y-4">
                <h2 className="font-heading text-xl flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Objectifs de la saison
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeSeason.objectives.map((objective) => {
                    const isCompleted = myProgress?.objectives_completed?.includes(objective.id);
                    return (
                      <Card 
                        key={objective.id} 
                        className={`card-base ${isCompleted ? 'bg-green-500/5 border-green-500/20' : ''}`}
                        data-testid={`objective-${objective.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isCompleted ? 'bg-green-500/20' : 'bg-secondary'}`}>
                                {isCompleted ? (
                                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                                ) : (
                                  <Target className="w-5 h-5 text-muted-foreground" />
                                )}
                              </div>
                              <div>
                                <h4 className="font-medium">{objective.title}</h4>
                                {objective.description && (
                                  <p className="text-sm text-muted-foreground mt-1">{objective.description}</p>
                                )}
                              </div>
                            </div>
                            <Badge className={isCompleted ? 'bg-green-500/20 text-green-500' : 'bg-primary/20 text-primary'}>
                              +{objective.points}
                            </Badge>
                          </div>
                          {!isCompleted && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full mt-3"
                              onClick={() => handleCompleteObjective(objective.id)}
                              data-testid={`complete-objective-${objective.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Marquer comme complété
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Leaderboard */}
            {joined && leaderboard.length > 0 && (
              <div className="space-y-4">
                <h2 className="font-heading text-xl flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Classement de la saison
                </h2>
                <Card className="card-base">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {leaderboard.map((entry, index) => (
                        <div 
                          key={entry.user_id}
                          className={`flex items-center gap-3 p-3 rounded-lg ${entry.user_id === user.id ? 'bg-primary/10 border border-primary/20' : 'bg-secondary/30'}`}
                        >
                          <div className="w-8 flex justify-center">
                            {getRankIcon(index)}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">{entry.user_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {entry.objectives_completed} objectifs complétés
                            </div>
                          </div>
                          <Badge className="bg-primary/20 text-primary">
                            {entry.points_earned} pts
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Participants count */}
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Users className="w-4 h-4" />
              <span>{activeSeason.participants?.length || 0} participants</span>
            </div>
          </div>
        )}

        {/* Admin: Season Management */}
        {isAdmin && allSeasons.length > 0 && (
          <div className="mt-8 space-y-4">
            <h2 className="font-heading text-xl flex items-center gap-2">
              <Compass className="w-5 h-5 text-blue-500" />
              Gestion des saisons
            </h2>
            <div className="grid gap-3">
              {allSeasons.map((season) => (
                <Card key={season.id} className={`card-base ${season.is_active ? 'border-blue-500/30' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{season.name}</h4>
                          {season.is_active && (
                            <Badge className="bg-blue-500/20 text-blue-500">Active</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {new Date(season.start_date).toLocaleDateString('fr-FR')} - {new Date(season.end_date).toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {season.participants?.length || 0} participants • {season.objectives?.length || 0} objectifs
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditSeason(season)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 hover:bg-red-500/10"
                          onClick={() => handleDeleteSeason(season.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Edit Season Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Modifier la saison</DialogTitle>
              <DialogDescription>Modifiez les détails de la saison</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateSeason} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Nom de la saison *</Label>
                  <Input 
                    value={newSeason.name} 
                    onChange={(e) => setNewSeason({ ...newSeason, name: e.target.value })} 
                    placeholder="Ex: La Traversée de Janvier"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de début *</Label>
                  <Input 
                    type="date"
                    value={newSeason.start_date} 
                    onChange={(e) => setNewSeason({ ...newSeason, start_date: e.target.value })} 
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin *</Label>
                  <Input 
                    type="date"
                    value={newSeason.end_date} 
                    onChange={(e) => setNewSeason({ ...newSeason, end_date: e.target.value })} 
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Description</Label>
                  <Textarea 
                    value={newSeason.description} 
                    onChange={(e) => setNewSeason({ ...newSeason, description: e.target.value })} 
                    placeholder="Description de la saison..."
                    className="bg-secondary/50"
                    rows={2}
                  />
                </div>
              </div>

              {/* Objectifs - Modification */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Objectifs de la saison
                </Label>
                
                {/* Liste des objectifs existants */}
                {newSeason.objectives.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {newSeason.objectives.map((obj, i) => (
                      <div key={obj.id || `obj-${i}`} className="flex items-center gap-2 p-3 bg-secondary/30 rounded-lg">
                        <Target className="w-4 h-4 text-primary flex-shrink-0" />
                        <Input 
                          value={obj.title}
                          onChange={(e) => updateObjective(i, 'title', e.target.value)}
                          className="flex-1 h-8 bg-background/50"
                          placeholder="Titre"
                        />
                        <Input 
                          type="number"
                          value={obj.points}
                          onChange={(e) => updateObjective(i, 'points', parseInt(e.target.value) || 0)}
                          className="w-20 h-8 bg-background/50"
                          placeholder="Pts"
                        />
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:bg-red-500/10 h-8 w-8 p-0"
                          onClick={() => removeObjective(i)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Ajouter un nouvel objectif */}
                <div className="flex gap-2 p-3 border border-dashed border-border rounded-lg">
                  <Input 
                    value={newObjective.id} 
                    onChange={(e) => setNewObjective({ ...newObjective, id: e.target.value })} 
                    placeholder="ID (ex: obj_1)"
                    className="bg-secondary/50 w-24"
                  />
                  <Input 
                    value={newObjective.title} 
                    onChange={(e) => setNewObjective({ ...newObjective, title: e.target.value })} 
                    placeholder="Titre du nouvel objectif"
                    className="bg-secondary/50 flex-1"
                  />
                  <Input 
                    type="number"
                    value={newObjective.points} 
                    onChange={(e) => setNewObjective({ ...newObjective, points: parseInt(e.target.value) || 10 })} 
                    placeholder="Points"
                    className="bg-secondary/50 w-20"
                  />
                  <Button type="button" onClick={addObjective} variant="outline" size="icon">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {newSeason.objectives.length} objectif(s) • Cliquez sur la corbeille pour supprimer
                </p>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setEditOpen(false); setEditingSeason(null); }}>Annuler</Button>
                <Button type="submit" className="bg-blue-500 text-white">Enregistrer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default SeasonsPage;
